import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Mail, Phone } from 'lucide-react';
import { Kicker, PageHero, SmchsLayout } from '@/components/smchs/Shell';
import { LIBRARY_EMAIL, LIBRARY_PHONE, telHref } from '@/components/smchs/contact-lib';

type Resource = { title: string; description: string; category: string; href?: string };

/* Most of these have no public address: access goes through a librarian,
   who sets up the login. So the page lists them and says how to get in,
   rather than linking each one somewhere it cannot go. Only a resource that
   is open to everyone carries an href. */
const GROUPS: { id: string; label: string; heading: string; items: Resource[] }[] = [
  {
    id: 'digital',
    label: 'Digital collection',
    heading: 'Databases, e-books and audiobooks.',
    items: [
      { title: 'JSTOR', description: 'Academic journals, books and primary sources.', category: 'Research' },
      { title: 'LinkedIn Learning', description: 'Video courses in software, design and business skills.', category: 'Learning' },
      { title: 'OverDrive / Libby', description: 'E-books and digital audiobooks from the library collection.', category: 'E-books' },
      { title: 'Audible collection', description: 'Audiobooks from the library collection.', category: 'Audiobooks' },
    ],
  },
  {
    id: 'educational',
    label: 'Subject help',
    heading: 'For a topic you are stuck on.',
    items: [
      {
        title: 'Khan Academy',
        description: 'Free lessons in maths, science, computing, history and more. Open to everyone, no login needed.',
        category: 'All subjects',
        href: 'https://www.khanacademy.org',
      },
      { title: 'Science lab materials', description: 'Lab worksheets and experiment guides for science courses.', category: 'Science' },
      { title: 'Historical archives', description: 'Primary source documents from key historical periods.', category: 'History' },
      { title: 'Language learning', description: 'Tools and materials for learning a foreign language.', category: 'Languages' },
    ],
  },
  {
    id: 'research',
    label: 'Research skills',
    heading: 'For the extended essay and beyond.',
    items: [
      { title: 'ProQuest', description: 'Dissertations, newspapers and periodicals, searchable in one place.', category: 'Database' },
      { title: 'Citation tools', description: 'Help managing sources and building a bibliography.', category: 'Writing' },
      { title: 'Statistical data', description: 'Statistical databases and tools for charting data.', category: 'Data' },
      { title: 'Research methods guides', description: 'Guides to research methods, subject by subject.', category: 'Methods' },
    ],
  },
];

const Resources: React.FC = () => (
  <SmchsLayout>
    <PageHero
      kickerLabel="Resources"
      title={
        <>
          Beyond the{' '}
          <span className="font-serif italic font-normal text-[var(--primary)]">shelves</span>.
        </>
      }
      tagline="Databases, e-books, audiobooks and study guides. For the subscriptions, ask a librarian at the desk or by email and they will set up your access."
      meta={
        <a
          href={`mailto:${LIBRARY_EMAIL}?subject=${encodeURIComponent('Access to a library resource')}`}
          className="inline-flex items-center gap-2 rounded-full bg-[var(--ink)] text-[var(--paper)] px-6 py-3 text-[15px] font-medium hover:opacity-90 transition-opacity"
        >
          Ask for access <ArrowRight className="w-4 h-4" />
        </a>
      }
    />

    {GROUPS.map((g) => (
      <section key={g.id} className="max-w-[1280px] mx-auto px-5 md:px-8 pt-16 md:pt-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          <div className="lg:col-span-4">
            <Kicker label={g.label} />
            <h2 className="mt-5 font-display font-[900] text-[1.9rem] md:text-[2.4rem] leading-[1.05] tracking-[-0.025em] max-w-[16ch]">
              {g.heading}
            </h2>
          </div>
          <ul className="lg:col-span-8 border-t border-[var(--border)]">
            {g.items.map((r) => (
              <li
                key={r.title}
                className="grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-6 py-5 border-b border-[var(--border)]"
              >
                <h3 className="sm:col-span-4 font-display font-bold text-[1.15rem] leading-snug text-[var(--ink)]">
                  {r.href ? (
                    <a
                      href={r.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 underline decoration-[var(--gold)] underline-offset-4 hover:text-[var(--primary)]"
                    >
                      {r.title} <ArrowUpRight className="w-4 h-4" />
                    </a>
                  ) : (
                    r.title
                  )}
                </h3>
                <p className="sm:col-span-6 text-[16px] leading-[1.55] text-[var(--ink-soft)]">{r.description}</p>
                <span className="sm:col-span-2 sm:text-right text-[13px] text-[var(--muted)]">{r.category}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    ))}

    <section className="max-w-[1280px] mx-auto px-5 md:px-8 py-20 md:py-28">
      <div className="rounded-2xl bg-[var(--ink)] text-[var(--paper)] p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
        <div className="md:col-span-7">
          <h2 className="font-display font-[900] text-[2rem] md:text-[2.75rem] leading-[1.02] tracking-[-0.025em]">
            Not sure where to start?
          </h2>
          <p className="mt-4 text-[16px] leading-[1.55] text-[var(--paper)]/75 max-w-[48ch]">
            A librarian can get you into any of these, or point you to something better for your topic.
          </p>
        </div>
        <div className="md:col-span-5 flex flex-col gap-3 text-[15px]">
          <a href={`mailto:${LIBRARY_EMAIL}`} className="inline-flex items-center gap-3 hover:opacity-80 transition-opacity">
            <Mail className="w-4 h-4 shrink-0" /> {LIBRARY_EMAIL}
          </a>
          <a href={telHref(LIBRARY_PHONE)} className="inline-flex items-center gap-3 hover:opacity-80 transition-opacity">
            <Phone className="w-4 h-4 shrink-0" /> {LIBRARY_PHONE}
          </a>
          <Link to="/about" className="inline-flex items-center gap-3 hover:opacity-80 transition-opacity">
            <ArrowRight className="w-4 h-4 shrink-0" /> Opening hours and directions
          </Link>
        </div>
      </div>
    </section>
  </SmchsLayout>
);

export default Resources;
